import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { IdentityTeamContact } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';

/**
 * Service to get and save team data.
 */
@Injectable()
export class IdentityTeamContactService {
    private readonly identityApiBaseUrl: string;

    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all team contacts
     */
    public getIndex(teamId: string, includes: string[] = null, queryParams: string[] = null)
    : Observable<IdentityTeamContact[]> {
        const apiRequestUrl: string = this.getApiUrl(teamId, null, includes, queryParams);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyData: any = response.data || [];
                return bodyData.map((contact: any) => new IdentityTeamContact().deserialize(contact));
            }));
    }

    /**
     * Get a single team contact
     */
    public get(
        identityTeamId: string,
        identityTeamContactId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityTeamContact> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamContactId, includes, queryParams);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityTeamContact().deserialize(response))));
    }

    /**
     * Add or update a team contact
     */
    public save(
        teamContact: IdentityTeamContact,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityTeamContact> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(teamContact.identityTeamId, teamContact.id, includes, queryParams);

        if (teamContact.id) {
            // Update existing Team
            response = this.http.patch(apiRequestUrl, teamContact.serialize());
        } else {
            // Add a new Team
            response = this.http.post(apiRequestUrl, teamContact.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new IdentityTeamContact()).deserialize(response)));
    }

    /**
     * Delete the specified contact.
     */
    public delete(identityTeamContact: IdentityTeamContact): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamContact.identityTeamId, identityTeamContact.id);
        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(teamId: string, teamContactId: string, includes: string[] = null, queryParams: string[] = null)
    : string {
        let url: string = `${this.identityApiBaseUrl}/teams/${teamId}/contacts/`;

        if (teamContactId) {
            url += teamContactId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

